import { RATING_NEUTRAL } from './constants.js';

/**
 * Turns individual peer ratings into the one number the rest of the app uses.
 *
 * A participant's performance is the average of what everyone who rated them
 * said. Where nobody has rated them, the admin's value from the Log screen
 * stands — so a game logged before anyone votes still works, and every rating
 * cast afterwards quietly sharpens it.
 *
 * Kept fractional on purpose. An average of 3.67 carries more than rounding it
 * to 4, and both the form maths and the match rating take a decimal happily.
 */
export function aggregateRatings(rows = []) {
  const byGame = {};
  rows.forEach((r) => {
    const forGame = (byGame[r.game_id] ||= {});
    const entry = (forGame[r.subject_member_id] ||= { total: 0, count: 0 });
    entry.total += r.score;
    entry.count += 1;
  });

  const out = {};
  Object.entries(byGame).forEach(([gameId, subjects]) => {
    out[gameId] = {};
    Object.entries(subjects).forEach(([memberId, { total, count }]) => {
      out[gameId][memberId] = { average: total / count, count };
    });
  });
  return out;
}

/**
 * What a player's performance should count as for a given game: the peer
 * average when anyone has rated them, the logged value otherwise.
 */
export function effectivePerformance(participant, ratingsForGame) {
  const peer = ratingsForGame?.[participant.memberId ?? participant.member_id];
  if (peer && peer.count > 0) return peer.average;
  return participant.performance ?? RATING_NEUTRAL;
}

/** Applies peer ratings across a list of games, ready for replay or display. */
export function applyPeerRatings(games, ratingsByGame) {
  return games.map((g) => ({
    ...g,
    participants: (g.participants || []).map((p) => {
      const count = ratingsByGame?.[g.id]?.[p.memberId ?? p.member_id]?.count || 0;
      return {
        ...p,
        performance: effectivePerformance(p, ratingsByGame?.[g.id]),
        raterCount: count,
        // Did anyone actually say anything? Either the group rated them, or an
        // older version of the app recorded a number when the game was logged.
        // The maths still falls back to 3 either way; this is for the screen,
        // which should not print a score nobody gave.
        rated: count > 0 || p.performance != null,
      };
    }),
  }));
}
